<script setup>
// Performans sekmesi: secili reklam hesabinin ozet metrikleri, kirilim
// tablosu ve anormallik uyarilari.
//
// Anormallik sorgusu ayri bir istekte ve ayri bir hata degiskeniyle tutuluyor;
// o arac desteklenmeyen bir hesapta patlarsa ana performans tablosu yine de
// gorunsun diye.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aracCalistir, diziyeCevir, paraBicimle, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const aralik = ref('last_7d');
const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

const anomaliler = ref([]);
const anomaliOzet = ref('');
const anomaliYukleniyor = ref(false);
const anomaliHata = ref('');

const ekArac = ref('');
const ekOzet = ref('');
const ekSatirlar = ref([]);
const ekYukleniyor = ref(false);
const ekHata = ref('');

const accountId = computed(() => route.params.accountId);

// Meta'nin kendi date_preset degerleri; sunucuya oldugu gibi gider.
const araliklar = computed(() => [
  { deger: 'last_7d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_7') },
  { deger: 'last_14d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_14') },
  { deger: 'last_30d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_30') },
  { deger: 'this_month', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.BU_AY') },
]);

const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

const topla = anahtar =>
  satirlar.value.reduce((t2, s) => t2 + Number(s?.[anahtar] || 0), 0);

// Toplamlar satirlardan hesaplaniyor: Meta kirilimli sorgularda hazir bir
// toplam satiri dondurmuyor, her kirilim ayri kayit olarak geliyor.
const ozet = computed(() => {
  const harcama = topla('spend');
  const gosterim = topla('impressions');
  const tiklama = topla('clicks');
  return {
    harcama,
    gosterim,
    tiklama,
    // Oranlari satirlarin ortalamasi yerine toplamlardan turetiyoruz;
    // gunluk oranlarin ortalamasi agirliksiz kalir ve yaniltir.
    ctr: gosterim ? (tiklama / gosterim) * 100 : 0,
    cpc: tiklama ? harcama / tiklama : 0,
  };
});

const sayiBicim = deger => sayiBicimle(deger);
const yuzdeBicim = deger => `${Number(deger || 0).toFixed(2)} %`;
// Meta tutarlari kurus geldigi icin bicimleme boluyor; ham deger aktarilir.
const paraBicim = deger => paraBicimle(deger, birim.value);

// Salt tarih metnini (2024-05-01) dogrudan Date'e vermek UTC olarak cozulup
// yerel saate cevrildigi icin gunu kaydirabiliyor; saat ekleyerek okuyoruz ve
// salt tarihte saat yazdirmiyoruz.
const tarihBicimle = deger => {
  const metin = String(deger ?? '');
  if (!metin) return '';
  const saltTarih = /^\d{4}-\d{2}-\d{2}$/.test(metin);
  const tarih = saltTarih ? new Date(`${metin}T00:00:00`) : new Date(metin);
  if (Number.isNaN(tarih.getTime())) return metin;
  return saltTarih
    ? tarih.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
    : tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const sutunlar = computed(() => [
  {
    anahtar: 'date_start',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.BASLANGIC'),
    // Meta ISO tarih dondurur; tabloda okunur bicimde duruyor.
    bicim: deger => (deger ? tarihBicimle(deger) : '—'),
  },
  {
    anahtar: 'date_stop',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.BITIS'),
    bicim: deger => (deger ? tarihBicimle(deger) : '—'),
  },
  {
    anahtar: 'spend',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.HARCAMA'),
    bicim: paraBicim,
  },
  {
    anahtar: 'impressions',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.GOSTERIM'),
    bicim: sayiBicim,
  },
  {
    anahtar: 'clicks',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.TIKLAMA'),
    bicim: sayiBicim,
  },
  {
    anahtar: 'ctr',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.CTR'),
    bicim: yuzdeBicim,
  },
  {
    anahtar: 'cpc',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.CPC'),
    bicim: paraBicim,
  },
]);

const kartlar = computed(() => [
  {
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.KART.HARCAMA'),
    deger: paraBicim(ozet.value.harcama),
  },
  {
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.KART.GOSTERIM'),
    deger: sayiBicim(ozet.value.gosterim),
  },
  {
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.KART.TIKLAMA'),
    deger: sayiBicim(ozet.value.tiklama),
  },
  {
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.KART.CTR'),
    deger: yuzdeBicim(ozet.value.ctr),
  },
  {
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.KART.CPC'),
    deger: paraBicim(ozet.value.cpc),
  },
]);

const anomalileriYukle = async () => {
  if (!props.hesap) return;
  anomaliYukleniyor.value = true;
  anomaliHata.value = '';
  try {
    const sonuc = await aracCalistir(
      accountId.value,
      'ads_insights_anomaly_signal',
      { account_id: props.hesap }
    );
    anomaliler.value = diziyeCevir(sonuc);
    anomaliOzet.value = sonuc?.ozet || '';
  } catch (e) {
    anomaliHata.value = e.response?.data?.error || e.message;
    anomaliler.value = [];
  } finally {
    anomaliYukleniyor.value = false;
  }
};

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_insights', {
      account_id: props.hesap,
      date_preset: aralik.value,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    satirlar.value = [];
  } finally {
    yukleniyor.value = false;
  }
  anomalileriYukle();
};

// Ek analiz araclari ham ozet metni dondurdugu icin tablo yerine once ozeti
// gosteriyoruz; dizi donerse tablo da cizilir.
const ekCalistir = async arac => {
  if (!props.hesap) return;
  ekArac.value = arac;
  ekYukleniyor.value = true;
  ekHata.value = '';
  ekOzet.value = '';
  ekSatirlar.value = [];
  try {
    const sonuc = await aracCalistir(accountId.value, arac, {
      account_id: props.hesap,
    });
    ekOzet.value = sonuc?.ozet || '';
    ekSatirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    ekHata.value = e.response?.data?.error || e.message;
  } finally {
    ekYukleniyor.value = false;
  }
};

const ekAraclar = computed(() => [
  {
    arac: 'ads_insights_performance_trend',
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.TREND'),
  },
  {
    arac: 'ads_insights_auction_ranking_benchmarks',
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.ACIK_ARTIRMA'),
  },
  {
    arac: 'ads_insights_industry_benchmark',
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.SEKTOR'),
  },
  {
    arac: 'ads_insights_advertiser_context',
    ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.BAGLAM'),
  },
]);

// Kaydin hazir aciklama cumlesi varsa basliga o cikiyor; bu dort alan
// basliga tasindigi icin alt listede tekrar edilmiyor.
const BASLIK_ALANLARI = ['message', 'description', 'signal', 'title'];

const anomaliMetni = kayit => {
  if (typeof kayit === 'string') return kayit;
  return (
    kayit?.message || kayit?.description || kayit?.signal || kayit?.title || ''
  );
};

// Anomali kaydinda gelebilen alanlarin okunur etiketleri. Listede olmayan
// anahtar oldugu gibi gosterilir; olmayan bir karsiligi uydurmak yaniltir.
const ANOMALI_ALAN_ETIKETLERI = {
  date: 'TARIH',
  date_start: 'BASLANGIC',
  date_stop: 'BITIS',
  metric: 'METRIK',
  value: 'DEGER',
  previous_value: 'ONCEKI_DEGER',
  change: 'DEGISIM',
  change_percent: 'DEGISIM_YUZDESI',
  severity: 'ONEM',
  level: 'DUZEY',
  campaign_name: 'KAMPANYA',
  adset_name: 'REKLAM_SETI',
  ad_name: 'REKLAM',
  spend: 'HARCAMA',
  impressions: 'GOSTERIM',
  clicks: 'TIKLAMA',
  ctr: 'CTR',
  cpc: 'CPC',
};

// Bilinen metrikler tablodakiyle ayni bicimde: ham 125000 yerine okunur tutar.
const ANOMALI_BICIMLERI = {
  spend: deger => paraBicim(deger),
  cpc: deger => paraBicim(deger),
  impressions: deger => sayiBicim(deger),
  clicks: deger => sayiBicim(deger),
  ctr: deger => yuzdeBicim(deger),
};

const TARIH_ALANLARI = new Set(['date', 'date_start', 'date_stop']);

const anomaliEtiket = anahtar => {
  const ek = ANOMALI_ALAN_ETIKETLERI[anahtar];
  return ek
    ? t(`META_ADS.ISTATISTIKLER.PERFORMANS.ANOMALI.ALAN.${ek}`)
    : anahtar;
};

// Bos deger '' donuyor ve satir hic basilmiyor: olmayan alani gostermek
// uyariyi okunmaz hale getirir.
const anomaliDeger = (anahtar, ham) => {
  if (ham === null || ham === undefined || ham === '') return '';
  if (typeof ham === 'boolean') {
    return ham
      ? t('META_ADS.ISTATISTIKLER.ORTAK.EVET')
      : t('META_ADS.ISTATISTIKLER.ORTAK.HAYIR');
  }
  if (Array.isArray(ham)) {
    if (!ham.length) return '';
    const duz = ham.every(o => o === null || typeof o !== 'object');
    return duz
      ? ham.join(', ')
      : t('META_ADS.ISTATISTIKLER.ORTAK.OGE_SAYISI', { sayi: ham.length });
  }
  if (typeof ham === 'object') {
    // Ic ice nesnede kullaniciya anlam tasiyan ad ya da kimlik; ikisi de
    // yoksa alan sayisi. Nesneyi acip basmak uyariyi okunmaz yapar.
    const ad = typeof ham.name === 'string' ? ham.name : '';
    const kimlik = ['string', 'number'].includes(typeof ham.id)
      ? String(ham.id)
      : '';
    return (
      ad ||
      kimlik ||
      t('META_ADS.ISTATISTIKLER.ORTAK.ALAN_SAYISI', {
        sayi: Object.keys(ham).length,
      })
    );
  }
  if (ANOMALI_BICIMLERI[anahtar]) return ANOMALI_BICIMLERI[anahtar](ham);
  if (TARIH_ALANLARI.has(anahtar)) return tarihBicimle(ham);
  return String(ham);
};

// Ham JSON yerine baslik + alan/deger satirlari; kullanici suslu parantez
// degil, uyarinin neye dair oldugunu okumali.
const anomaliListesi = computed(() =>
  anomaliler.value.map(kayit => {
    const metin = anomaliMetni(kayit);
    const nesne =
      kayit && typeof kayit === 'object' && !Array.isArray(kayit) ? kayit : null;
    const alanlar = nesne
      ? Object.entries(nesne)
          .filter(([anahtar]) => !BASLIK_ALANLARI.includes(anahtar))
          .map(([anahtar, ham]) => ({
            anahtar,
            etiket: anomaliEtiket(anahtar),
            deger: anomaliDeger(anahtar, ham),
          }))
          .filter(alan => alan.deger !== '')
      : [];
    return { metin, alanlar };
  })
);

watch(() => props.hesap, yukle);
watch(aralik, yukle);
onMounted(yukle);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.HESAP_SEC') }}
    </p>

    <template v-else>
      <div class="flex flex-wrap items-center gap-3">
        <label class="text-sm text-n-slate-11" for="performans-aralik">
          {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.TARIH_ARALIGI') }}
        </label>
        <select
          id="performans-aralik"
          v-model="aralik"
          class="!mb-0 !w-auto min-w-[180px]"
        >
          <option v-for="a in araliklar" :key="a.deger" :value="a.deger">
            {{ a.ad }}
          </option>
        </select>
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.ISTATISTIKLER.PERFORMANS.YENILE')"
          @click="yukle"
        />
      </div>

      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div
          v-for="k in kartlar"
          :key="k.ad"
          class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak"
        >
          <span class="text-xs text-n-slate-11">{{ k.ad }}</span>
          <span class="text-lg font-medium text-n-slate-12">{{ k.deger }}</span>
        </div>
      </div>

      <section class="flex flex-col gap-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.TABLO_BASLIK') }}
        </h2>
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="sutunlar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.ISTATISTIKLER.PERFORMANS.VERI_YOK')"
        />
      </section>

      <section class="flex flex-col gap-2 pt-4 border-t border-n-weak">
        <div class="flex items-center gap-3">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.ANOMALI.BASLIK') }}
          </h2>
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-activity"
            :label="t('META_ADS.ISTATISTIKLER.PERFORMANS.ANOMALI.KONTROL_ET')"
            @click="anomalileriYukle"
          />
        </div>

        <p v-if="anomaliHata" class="text-sm text-n-ruby-11">
          {{ anomaliHata }}
        </p>

        <div v-if="anomaliYukleniyor" class="flex p-4">
          <Spinner class="text-n-brand" />
        </div>

        <template v-else>
          <ul v-if="anomaliListesi.length" class="flex flex-col gap-2">
            <li
              v-for="(kayit, i) in anomaliListesi"
              :key="i"
              class="flex flex-col gap-2 p-3 text-sm border rounded-lg border-n-weak"
            >
              <p v-if="kayit.metin" class="whitespace-pre-line text-n-slate-12">
                {{ kayit.metin }}
              </p>
              <!--
                Ham JSON yerine alan/deger satirlari: uyarinin hangi metrikte
                ve hangi tarihte oldugu tek bakista okunuyor.
              -->
              <dl
                v-if="kayit.alanlar.length"
                class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1"
              >
                <template v-for="alan in kayit.alanlar" :key="alan.anahtar">
                  <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
                  <dd class="break-all text-n-slate-12">{{ alan.deger }}</dd>
                </template>
              </dl>
              <p
                v-if="!kayit.metin && !kayit.alanlar.length"
                class="text-n-slate-11"
              >
                {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.ANOMALI.AYRINTI_YOK') }}
              </p>
            </li>
          </ul>
          <p v-else-if="anomaliOzet" class="text-sm whitespace-pre-line text-n-slate-11">
            {{ anomaliOzet }}
          </p>
          <p v-else class="text-sm text-n-slate-11">
            {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.ANOMALI.YOK') }}
          </p>
        </template>
      </section>

      <section class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.BASLIK') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.EK.ACIKLAMA') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="e in ekAraclar"
            :key="e.arac"
            variant="faded"
            size="sm"
            :label="e.ad"
            @click="ekCalistir(e.arac)"
          />
        </div>

        <p v-if="ekHata" class="text-sm text-n-ruby-11">{{ ekHata }}</p>

        <div v-if="ekYukleniyor" class="flex p-4">
          <Spinner class="text-n-brand" />
        </div>

        <template v-else-if="ekArac">
          <p v-if="ekOzet" class="text-sm whitespace-pre-line text-n-slate-11">
            {{ ekOzet }}
          </p>
          <!--
            Ozet de satir da yoksa tablo yine ciziliyor: aksi halde dugmeye
            basan kullanici bombos bir alan gorur ve isteginin calisip
            calismadigini anlayamaz.
          -->
          <AracTablosu
            v-if="ekSatirlar.length || !ekOzet"
            :satirlar="ekSatirlar"
            :bos-metin="t('META_ADS.ISTATISTIKLER.PERFORMANS.VERI_YOK')"
          />
        </template>
      </section>
    </template>
  </div>
</template>
